import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiContext";
import { queryKeys } from "../../api/queryKeys";
import {
  employeeImportSchema,
  invitationDispatchSchema,
  invitationStatusResponseSchema,
  invitationsSchema,
  setupChecklistSchema,
  workTimeModelSchema,
  workTimeModelTemplatesSchema,
} from "../../api/schemas";
import type {
  EmployeeImport,
  Invitations,
  SetupChecklist,
  SetupStep,
  WorkTimeModel,
  WorkTimeModelTemplates,
} from "../../api/schemas";

/** A CSV file to check or import. */
export interface ImportInput {
  readonly csv: string;
  readonly checkOnly: boolean;
}

/** One employee entered in the wizard. */
export interface SetupEmployeeInput {
  readonly personnelNumber: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string | null;
  readonly entryDate: string;
  readonly workTimeModelId: string;
  readonly establishmentId: string;
}

/**
 * Loads the setup checklist (T-133).
 *
 * @param enabled - Whether the person may read it (HR).
 * @returns The query result.
 */
export function useSetupChecklist(enabled = true): UseQueryResult<SetupChecklist> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.setupChecklist,
    queryFn: ({ signal }) =>
      client.get("/api/v1/setup/checklist", setupChecklistSchema, { signal }),
    enabled,
  });

  return query;
}

/**
 * Confirms or withdraws a step of the checklist.
 *
 * @returns The mutation.
 */
export function useConfirmStep(): UseMutationResult<
  SetupChecklist,
  Error,
  { readonly step: SetupStep; readonly confirmed: boolean }
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: { readonly step: SetupStep; readonly confirmed: boolean }) =>
      client.post(
        `/api/v1/setup/checklist/${input.step}:${input.confirmed ? "confirm" : "withdraw"}`,
        {},
        setupChecklistSchema,
      ),
    onSuccess: (checklist) => {
      queryClient.setQueryData(queryKeys.setupChecklist, checklist);
    },
  });

  return mutation;
}

/**
 * Loads the work time model templates.
 *
 * @returns The query result.
 */
export function useModelTemplates(): UseQueryResult<WorkTimeModelTemplates> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.setupTemplates,
    queryFn: ({ signal }) =>
      client.get("/api/v1/setup/work-time-model-templates", workTimeModelTemplatesSchema, {
        signal,
      }),
    staleTime: Infinity,
  });

  return query;
}

/**
 * Creates work time models from templates.
 *
 * @returns The mutation.
 */
export function useCreateModels(): UseMutationResult<WorkTimeModel[], Error, readonly string[]> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (templateKeys: readonly string[]) =>
      client.post("/api/v1/setup/work-time-models", { templateKeys }, workTimeModelSchema.array()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.workTimeModels }),
  });

  return mutation;
}

/**
 * Checks or imports a CSV file of employees.
 *
 * @returns The mutation.
 */
export function useImportEmployees(): UseMutationResult<EmployeeImport, Error, ImportInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: ImportInput) =>
      client.post("/api/v1/setup/employees:import", input, employeeImportSchema),
    onSuccess: async (result) => {
      if (result.created > 0) {
        await queryClient.invalidateQueries({ queryKey: ["setup"] });
        await queryClient.invalidateQueries({ queryKey: queryKeys.employees });
      }
    },
  });

  return mutation;
}

/**
 * Adds one employee.
 *
 * @returns The mutation.
 */
export function useAddEmployee(): UseMutationResult<EmployeeImport, Error, SetupEmployeeInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (input: SetupEmployeeInput) =>
      client.post("/api/v1/setup/employees", input, employeeImportSchema),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["setup"] });
      await queryClient.invalidateQueries({ queryKey: queryKeys.employees });
    },
  });

  return mutation;
}

/**
 * Loads the invitations.
 *
 * @returns The query result.
 */
export function useInvitations(): UseQueryResult<Invitations> {
  const client = useApiClient();
  const query = useQuery({
    queryKey: queryKeys.invitations,
    queryFn: ({ signal }) => client.get("/api/v1/setup/invitations", invitationsSchema, { signal }),
  });

  return query;
}

/**
 * Sends the open invitations, or one invitation again.
 *
 * @returns The mutation; the argument is an employee to invite again, or `null` for all open ones.
 */
export function useSendInvitations(): UseMutationResult<unknown, Error, string | null> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (employeeId: string | null): Promise<unknown> => {
      const response: unknown =
        employeeId === null
          ? await client.post("/api/v1/setup/invitations:send", {}, invitationDispatchSchema)
          : await client.post(
              `/api/v1/setup/invitations/${encodeURIComponent(employeeId)}:resend`,
              {},
              invitationStatusResponseSchema,
            );

      return response;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["setup"] }),
  });

  return mutation;
}
